// stores/chatStore.ts
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { toSafeMarkdownHtml } from '#layers/chat-ui/app/utils/markdown'
import { useAuth } from '~/composables/useAuth'
import { useFrontApi } from '~/composables/useFrontApi'
import { useMbtiStore } from '~/stores/mbtiStore'
import type { ProfessionRecommendationObj, ProgramRecommendationObj } from '~/types/school'
import type { ConversationMessageDto } from '~/types/Conversation'
import type {
  DimensionThemeProgress,
  LolaPushedMessage,
  RememberedItem,
} from '~/types/NotificationsHub'
import { useAnalysisStore } from '~/stores/analysisStore'
import { useAgentInformationsStore } from '~/stores/agentInformationsStore'
import { useRecommendationStore } from '~/stores/recommendationStore'
import { TRACKING_EVENTS } from '~/constants/trackingEvents'
import type { LolaMessageSource } from '~/types/Tracking'
import type { QualificationCriteriaQuestion } from '~/types/UserQualifications'
import { useChatAttachmentsStore } from '~/stores/chatAttachmentsStore'
import { normalizeBaseUrl } from '~/utils/helpers'
import type { ChatAttachmentRequest } from '~/types/ChatAttachment'
import { buildAttachmentsPayload } from '~/utils/chatAttachment'

// ── Types ─────────────────────────────────────────────────

type MessageRole = 'user' | 'assistant'
type ConversationState = 'exploring' | 'narrowing' | 'decided'
type SuggestionType =
  | 'convButtons'
  | 'bottomCheckBoxes'
  | 'bottomUniqueChoice'
  | 'convButtonsCheckBoxes'

export interface SuggestionOption {
  id: string
  label: string
}

export interface Suggestions {
  type: SuggestionType
  options: Array<string | SuggestionOption>
}

/** Métadonnées d'une pièce jointe affichée dans une bulle de message utilisateur. */
export interface ChatMessageAttachment {
  name: string
  type: string
  size: number
}

export interface ChatMessage {
  id: number
  role: MessageRole
  text: string
  renderedHtml: string | null
  streaming: boolean
  intent?: string
  confidence?: number
  suggestions?: Suggestions | null
  programsrecos?: ProgramRecommendationObj[] | null
  professionsrecos?: ProfessionRecommendationObj[] | null
  attachments?: ChatMessageAttachment[]
}

/** Corps de la requête `POST /api/Conversation/chat`. */
interface ChatRequestBody {
  message: string
  attachments?: ChatAttachmentRequest[]
}

type ConversationRememberedItemDto = {
  quote: string | null
  note: string | null
}

type ConversationHistoryDto = {
  schoolId: string | null
  messages: ConversationMessageDto[] | null
  remembered: ConversationRememberedItemDto[] | null
  lastSuggestedAnswers: string[] | null
  lastSuggestedAnswersMultiple: boolean
}

type NextDimensionQuestionDto = {
  messageId: string | null
  question: string | null
  suggestions: string[] | null
  createdDate: string | null
  updatedTheme: DimensionThemeProgress | null
}

interface SseStatusData {
  message: string
}
interface SseTextData {
  chunk: string
}
interface SseMetaData {
  intent?: string
  confidence?: number
  conversationState?: ConversationState
  suggestions?: string[]
  multiple: boolean
  Programsrecos?: ProgramRecommendationObj[]
  Professionsrecos?: ProfessionRecommendationObj[]
}
interface SseErrorData {
  message?: string
}

// ── SSE status labels ─────────────────────────────────────
const STATUS_LABELS: Record<string, string | null | undefined> = {
  thinking: 'Analyse en cours',
  searching: 'Recherche en cours',
  responding: null, // null = fin spinner, début texte
  suggesting: undefined, // ignoré
}

export const useChatStore = defineStore('chat', () => {
  // ── State ───────────────────────────────────────────────
  const messages: Ref<ChatMessage[]> = ref([])
  const statusLabel = ref<string | null>(null)
  const conversationState = ref<ConversationState | null>(null)

  const isPersonalityTestActive: ComputedRef<boolean> = computed((): boolean => {
    for (let index = messages.value.length - 1; index >= 0; index--) {
      const message = messages.value[index]
      // L'intention d'une bulle n'arrive qu'à la fin du flux : on lit la dernière réponse terminée.
      if (message?.role === 'assistant' && !message.streaming)
        return message.intent === 'personality_test'
    }
    return false
  })
  const isPersonalityTestResultPending: ComputedRef<boolean> = computed((): boolean => {
    for (let index = messages.value.length - 1; index >= 0; index--) {
      const message: ChatMessage | undefined = messages.value[index]
      if (message?.role === 'assistant' && !message.streaming) {
        return (
          message.intent === 'personality_test' &&
          !message.suggestions &&
          useMbtiStore().analysisPending
        )
      }
    }
    return false
  })
  const programsrecos: Ref<ProgramRecommendationObj[] | null> = ref(null)
  const professionsrecos: Ref<ProfessionRecommendationObj[] | null> = ref(null)
  const isLoading = ref(false)
  const isStreaming = ref(false)
  const error = ref<string | null>(null)
  /** Brouillon de l'input partagé, rempli par les boutons convButtonsCheckBoxes. */
  const inputDraft: Ref<string> = ref('')

  /** Formations affichées dans le panneau détail (reco Lola ou propositions profil). */
  const displayedProgramRecommendations: ComputedRef<ProgramRecommendationObj[]> = computed(
    (): ProgramRecommendationObj[] => {
      if (programsrecos.value != null && programsrecos.value.length > 0) {
        return programsrecos.value
      }
      return new Array<ProgramRecommendationObj>()
    },
  )

  /** Métiers affichés dans le panneau détail (reco Lola ou propositions profil). */
  const displayedProfessionRecommendations: ComputedRef<ProfessionRecommendationObj[]> = computed(
    (): ProfessionRecommendationObj[] => {
      if (professionsrecos.value != null && professionsrecos.value.length > 0) {
        return professionsrecos.value
      }
      return new Array<ProfessionRecommendationObj>()
    },
  )

  // ── Helpers ─────────────────────────────────────────────
  let _msgIdCounter = 0
  /**
   * Génère un identifiant incrémental pour les messages.
   * @returns {number} Un nouvel identifiant unique dans la session.
   */
  function _newId(): number {
    return ++_msgIdCounter
  }

  /**
   * Parse un bloc SSE brut (séparé par double saut de ligne).
   * @param {string} block Contenu d'un bloc SSE (lignes `event:`/`data:`).
   * @returns {{ event: string; data: unknown }} Événement et payload JSON éventuel.
   */
  function _parseSSEBlock(block: string): { event: string; data: unknown } {
    let event = 'message'
    let data: unknown = null
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim()
      if (line.startsWith('data:')) {
        try {
          data = JSON.parse(line.slice(5).trim())
        } catch {
          /* ignore */
        }
      }
    }
    return { event, data }
  }

  /**
   * fetch SSE vers FrontApi (en-tête X-Api-Key + token MSAL) — fetch natif obligatoire pour le streaming body.
   * @param {string} path Chemin API (ex: `/api/Conversation/chat`).
   * @param {unknown} body Payload JSON envoyé en POST.
   * @returns {Promise<Response>} Réponse `fetch` (streaming) si HTTP OK.
   */
  async function _ssePost(path: string, body: unknown): Promise<Response> {
    const { getApiToken } = useAuth()
    const config = useRuntimeConfig()
    const baseURL = normalizeBaseUrl(config.public.frontApiBaseUrl as string)
    const token = await getApiToken()

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Api-Key': config.public.frontXApiKey as string,
    }
    if (token) headers['Authorization'] = `Bearer ${token}`

    const res = await fetch(`${baseURL}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res
  }

  // ── Actions ─────────────────────────────────────────────

  /**
   * Charge l'historique une seule fois : la promesse est partagée entre tous les appelants.
   */
  let historyPromise: Promise<void> | null = null
  function loadHistory(): Promise<void> {
    if (historyPromise) return historyPromise
    historyPromise = _loadHistory()
    return historyPromise
  }

  async function _loadHistory(): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      const frontApi = useFrontApi()
      const { data } = await frontApi.get<ConversationHistoryDto>('/api/Conversation/history')

      const apiMessages: ConversationMessageDto[] = data.messages ?? []
      messages.value = apiMessages.map(
        (message: ConversationMessageDto): ChatMessage => ({
          id: _newId(),
          role: message.role === 'assistant' ? 'assistant' : 'user',
          text: message.content ?? '',
          renderedHtml:
            message.role === 'assistant' ? toSafeMarkdownHtml(message.content ?? '') : null,
          streaming: false,
        }),
      )

      const lastSuggestedAnswers: string[] = data.lastSuggestedAnswers ?? []
      if (lastSuggestedAnswers.length > 0) {
        const assistantMessages: ChatMessage[] = messages.value.filter(
          (message: ChatMessage): boolean => message.role === 'assistant',
        )
        const lastAssistantMessage: ChatMessage | undefined =
          assistantMessages[assistantMessages.length - 1]
        if (lastAssistantMessage) {
          lastAssistantMessage.suggestions = {
            type: data.lastSuggestedAnswersMultiple ? 'convButtonsCheckBoxes' : 'convButtons',
            options: lastSuggestedAnswers,
          }
        }
      }

      useAgentInformationsStore().setRemembered(
        (data.remembered ?? []).map(
          (item: ConversationRememberedItemDto): RememberedItem => ({
            quote: item.quote ?? '',
            note: item.note,
          }),
        ),
      )
    } catch (err) {
      error.value = (err as Error).message
      console.error('[chatStore] loadHistory:', err)
      historyPromise = null // autorise un nouvel essai après échec
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Envoie le message utilisateur et consomme le stream SSE.
   * L'historique est géré entièrement côté back (CosmosDB).
   * @param {string} text Message utilisateur.
   * @param {LolaMessageSource} source Origine du message (saisie, suggestion, carte programme).
   * @param {File[]} attachments Pièces jointes accompagnant le message.
   * @returns {Promise<void>} Résolution quand le stream est terminé.
   */
  async function sendMessage(
    text: string,
    source: LolaMessageSource = 'input',
    attachments: File[] = [],
  ): Promise<void> {
    if ((!text.trim() && attachments.length === 0) || isStreaming.value) return

    // Réinitialise le brouillon pré-rempli par les convButtonsCheckBoxes.
    inputDraft.value = ''

    const mbtiStore = useMbtiStore()
    const analysisStore = useAnalysisStore()
    const recommendationStore = useRecommendationStore()
    const { track } = useTracking()

    error.value = null
    messages.value.push({
      id: _newId(),
      role: 'user',
      text,
      renderedHtml: null,
      streaming: false,
      attachments: attachments.length
        ? attachments.map((file: File) => ({ name: file.name, type: file.type, size: file.size }))
        : undefined,
    })

    const userMessageIndex: number = messages.value.filter((m) => m.role === 'user').length
    track(TRACKING_EVENTS.LOLA_MESSAGE_SENT, {
      messageIndex: userMessageIndex,
      source,
      attachmentCount: attachments.length,
    })

    isStreaming.value = true
    statusLabel.value = STATUS_LABELS.thinking ?? null

    let assistantMsgId: number | null = null
    let fullText = ''
    let metadataSnap: SseMetaData | null = null

    try {
      await loadHistory()

      const body: ChatRequestBody = { message: text }
      if (attachments.length) {
        body.attachments = await buildAttachmentsPayload(attachments)
      }

      const res = await _ssePost('/api/Conversation/chat', body)
      const reader = res.body!.getReader()
      const decoder = new TextDecoder()
      let sseBuffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        sseBuffer += decoder.decode(value, { stream: true })
        const blocks = sseBuffer.split('\n\n')
        sseBuffer = blocks.pop() ?? ''

        for (const block of blocks) {
          const { event, data } = _parseSSEBlock(block)
          if (!data) continue

          switch (event) {
            case 'status': {
              const label = STATUS_LABELS[(data as SseStatusData).message]
              if (label === null) {
                // 'responding' : fin spinner, créer la bulle assistant
                statusLabel.value = null
                assistantMsgId = _newId()
                messages.value.push({
                  id: assistantMsgId,
                  role: 'assistant',
                  text: '',
                  renderedHtml: null,
                  streaming: true,
                  suggestions: null,
                })
              } else if (label) {
                statusLabel.value = label
              }
              break
            }

            case 'text': {
              // Sécurité : créer la bulle si 'responding' n'est pas arrivé
              if (assistantMsgId === null) {
                statusLabel.value = null
                assistantMsgId = _newId()
                messages.value.push({
                  id: assistantMsgId,
                  role: 'assistant',
                  text: '',
                  renderedHtml: null,
                  streaming: true,
                  suggestions: null,
                })
              }
              fullText += (data as SseTextData).chunk
              const msg = messages.value.find((m) => m.id === assistantMsgId)
              if (msg) msg.text = fullText
              break
            }

            case 'metadata': {
              metadataSnap = data as SseMetaData
              break
            }

            case 'done':
              break

            case 'error': {
              statusLabel.value = null
              const errMsg = (data as SseErrorData).message ?? 'Erreur inconnue'
              if (assistantMsgId !== null) {
                const msg = messages.value.find((m) => m.id === assistantMsgId)
                if (msg) {
                  msg.text = `Erreur : ${errMsg}`
                  msg.streaming = false
                }
              } else {
                messages.value.push({
                  id: _newId(),
                  role: 'assistant',
                  text: `Erreur : ${errMsg}`,
                  renderedHtml: null,
                  streaming: false,
                })
              }
              break
            }
          }
        }
      }

      // ── Fin du stream ──────────────────────────────────
      statusLabel.value = null

      const finalMsg = messages.value.find((m) => m.id === assistantMsgId)
      if (finalMsg) {
        finalMsg.streaming = false
        finalMsg.renderedHtml = toSafeMarkdownHtml(fullText)

        console.log('[chatStore] Message final reçu, metadataSnap:', metadataSnap)

        const wasPersonalityTestInProgress: boolean = mbtiStore.testState === 'progress'
        const messageIntent: string | undefined = metadataSnap?.intent

        if (metadataSnap) {
          if (messageIntent) {
            finalMsg.intent = messageIntent
            recommendationStore.fetch()
          }
          if (metadataSnap.confidence != null) finalMsg.confidence = metadataSnap.confidence
          if (metadataSnap.conversationState)
            conversationState.value = metadataSnap.conversationState
          if (metadataSnap.suggestions?.length) {
            finalMsg.suggestions = {
              type: metadataSnap.multiple ? 'convButtonsCheckBoxes' : 'convButtons',
              options: metadataSnap.suggestions,
            }
          }
          if (metadataSnap.Programsrecos != null) programsrecos.value = metadataSnap.Programsrecos
          if (metadataSnap.Professionsrecos != null)
            professionsrecos.value = metadataSnap.Professionsrecos
        }

        const isPersonalityTestIntent: boolean = messageIntent === 'personality_test'
        const shouldRevealPersonalityReport: boolean =
          isPersonalityTestIntent || wasPersonalityTestInProgress

        if (messageIntent || wasPersonalityTestInProgress) {
          void analysisStore.fetchPersonalityAnalysis()
          void mbtiStore.fetch({ revealReportIfComplete: shouldRevealPersonalityReport })
        }

        // Réponse de Lola complète : porte les signaux d'intention (funnel produit).
        track(TRACKING_EVENTS.LOLA_MESSAGE_RECEIVED, {
          intent: metadataSnap?.intent,
          confidence: metadataSnap?.confidence,
          conversationState: metadataSnap?.conversationState,
        })

        // Moment de valeur livrée : des recommandations ont été affichées.
        const programCount: number = metadataSnap?.Programsrecos?.length ?? 0
        const professionCount: number = metadataSnap?.Professionsrecos?.length ?? 0
        if (programCount > 0 || professionCount > 0) {
          track(TRACKING_EVENTS.RECOMMENDATIONS_SHOWN, { programCount, professionCount })
        }
      }

      if (attachments.length) {
        void useChatAttachmentsStore().fetchAttachments()
      }
    } catch (err) {
      statusLabel.value = null
      error.value = (err as Error).message
      console.error('[chatStore] sendMessage:', err)
      track(TRACKING_EVENTS.LOLA_ERROR, { message: (err as Error).message })

      if (assistantMsgId === null) {
        messages.value.push({
          id: _newId(),
          role: 'assistant',
          text: `Erreur : ${(err as Error).message}`,
          renderedHtml: null,
          streaming: false,
        })
      }
    } finally {
      isStreaming.value = false
    }
  }

  /**
   * Envoie une suggestion (réponse rapide)
   * @param {string} text Suggestion à envoyer comme message utilisateur.
   * @returns {Promise<void>} Délègue à `sendMessage`.
   */
  function sendSuggestion(text: string): Promise<void> {
    return sendMessage(text, 'suggestion')
  }

  /**
   * Répond à une question du drawer filtres : message à Lola, puis enregistrement du critère.
   * @param {QualificationCriteriaQuestion} question Question répondue.
   * @param {string} option Option choisie.
   * @returns {Promise<void>} Résolution après l'enregistrement du critère.
   */
  async function answerCriteriaQuestion(
    question: QualificationCriteriaQuestion,
    option: string,
  ): Promise<void> {
    const { track } = useTracking()
    track(TRACKING_EVENTS.LOLA_SEARCH_FILTER_CRITERION_ADDED, {
      group: question.key,
      value: option,
    })
    sendSuggestion(question.lolaMessagePrefix + option)
    await useAgentInformationsStore().addCriterion(
      question.category,
      option,
      question.isMultipleChoice,
    )
  }

  /**
   * Demande à Lola la prochaine question d'une dimension (`POST /api/dimensions/{themeId}/next-question`)
   * et l'ajoute au fil comme message assistant (avec ses suggestions).
   * @param {string} themeId Identifiant de la dimension.
   * @returns {Promise<void>} Résolution après ajout du message.
   */
  async function askDimensionQuestion(themeId: string): Promise<void> {
    if (isStreaming.value) return
    try {
      const { data } = await useFrontApi().post<NextDimensionQuestionDto>(
        `/api/dimensions/${themeId}/next-question`,
      )
      const question: string = data.question ?? ''
      if (question === '') return

      messages.value.push({
        id: _newId(),
        role: 'assistant',
        text: question,
        renderedHtml: toSafeMarkdownHtml(question),
        streaming: false,
        suggestions: data.suggestions?.length
          ? { type: 'convButtons', options: data.suggestions }
          : null,
      })

      if (data.updatedTheme) {
        useAgentInformationsStore().applyDimensions([data.updatedTheme])
      }
    } catch (err) {
      console.error('[chatStore] askDimensionQuestion:', err)
    }
  }

  /**
   * Ajoute au fil un message que Lola envoie hors SSE (SignalR `LolaMessageReady`, ex. le résultat du test MBTI).
   * @param {LolaPushedMessage} pushedMessage - Message déjà persisté côté back, avec ses suggestions de réponse.
   * @returns {Promise<void>} Résolution après ajout du message.
   */
  async function applyPushedMessage(pushedMessage: LolaPushedMessage): Promise<void> {
    useMbtiStore().setAnalysisPending(false)

    // Historique pas encore demandé : il contiendra ce message, déjà persisté.
    if (!historyPromise) return
    await historyPromise

    const content: string = pushedMessage.message.content ?? ''
    if (content === '') return

    // Poussé pendant le chargement de l'historique, le message peut déjà y figurer.
    const lastMessage: ChatMessage | undefined = messages.value[messages.value.length - 1]
    if (lastMessage?.text === content) return

    messages.value.push({
      id: _newId(),
      role: 'assistant',
      text: content,
      renderedHtml: toSafeMarkdownHtml(content),
      streaming: false,
      suggestions: pushedMessage.lastSuggestedAnswers.length
        ? { type: 'convButtons', options: pushedMessage.lastSuggestedAnswers }
        : null,
    })
  }

  return {
    messages,
    statusLabel,
    conversationState,
    isPersonalityTestActive,
    isPersonalityTestResultPending,
    programsrecos,
    professionsrecos,
    displayedProgramRecommendations,
    displayedProfessionRecommendations,
    isLoading,
    isStreaming,
    error,
    inputDraft,
    loadHistory,
    sendMessage,
    sendSuggestion,
    answerCriteriaQuestion,
    askDimensionQuestion,
    applyPushedMessage,
  }
})
